import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { theme } from '../theme'

// Shared overlay+panel shell for all modals. Handles two-phase mount so the
// backdrop and panel can fade/scale in and out. Children render inside the
// panel — bring your own header/body/footer.
//
// `panelStyle` lets each modal set its own size constraints (width, maxHeight,
// padding, etc.). Common chrome (background, border, radius, shadow, flex
// column, transition) is supplied here and can be overridden if needed.

const FADE_MS = 320
const PANEL_MS = 380

export function ModalShell({
  open, onClose, panelStyle, backdropPadding = 20, zIndex = 60, autoHeight = false, children,
}: {
  open: boolean
  onClose: () => void
  panelStyle?: CSSProperties
  backdropPadding?: number
  zIndex?: number
  // When true, observe content height and animate the panel between sizes
  // as children mount/unmount. Use for intrinsic-sized modals (no maxHeight
  // + internal scroll). Padding/gap from panelStyle move to the inner
  // wrapper so the outer panel can take an explicit pixel height.
  autoHeight?: boolean
  children: ReactNode
}) {
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const innerRef = useRef<HTMLDivElement>(null)
  const [innerH, setInnerH] = useState<number | null>(null)
  // Visual-viewport rect — tracks the area not occluded by the on-screen
  // keyboard. When the keyboard opens, this shrinks and offsetTop may shift;
  // we resize the backdrop to match so the modal recenters above the keyboard
  // (and top-anchors when there's a meaningful keyboard inset, so the picker
  // dropdown inside the modal has room to extend downward).
  const [vv, setVv] = useState<{ top: number; left: number; width: number; height: number } | null>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      // Double rAF so the browser paints the starting (opacity 0 / scaled)
      // state before we flip to the visible state. A single rAF can batch
      // into the mount paint and skip the transition entirely.
      let id2 = 0
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setVisible(true))
      })
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    setVisible(false)
    const t = window.setTimeout(() => setMounted(false), PANEL_MS + 40)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!mounted) return
    const vp = window.visualViewport
    if (!vp) return
    const update = () => setVv({
      top: vp.offsetTop, left: vp.offsetLeft, width: vp.width, height: vp.height,
    })
    update()
    vp.addEventListener('resize', update)
    vp.addEventListener('scroll', update)
    return () => {
      vp.removeEventListener('resize', update)
      vp.removeEventListener('scroll', update)
    }
  }, [mounted])

  useEffect(() => {
    if (!mounted || !autoHeight) return
    const el = innerRef.current
    if (!el) return
    const measure = () => setInnerH(el.offsetHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mounted, autoHeight])

  if (!mounted) return null

  // Split padding/gap off panelStyle when autoHeight is on, so the inner
  // wrapper carries them. That way the outer panel's explicit height
  // (= inner offsetHeight) matches what's actually painted, and the panel
  // can transition between sizes without padding double-counting.
  // NOTE: percentage maxHeight ('86%') will not resolve on inner — its
  // containing block is the auto-sized outer. Use viewport units ('86dvh').
  let outerPanel: CSSProperties = panelStyle ?? {}
  let innerStyle: CSSProperties = { display: 'flex', flexDirection: 'column' }
  if (autoHeight && panelStyle) {
    // Move only the *defined* padding/gap/height keys. Spreading them
    // unconditionally with `undefined` values is a footgun: React turns
    // each `paddingTop: undefined` into `el.style.paddingTop = ''`, which
    // CLEARS that longhand AFTER the `padding` shorthand was applied —
    // wiping all the padding off the element.
    const moveKeys = [
      'padding','paddingTop','paddingRight','paddingBottom','paddingLeft',
      'paddingBlock','paddingInline','gap','rowGap','columnGap','maxHeight',
    ] as const
    const next: Record<string, unknown> = { ...innerStyle, boxSizing: 'border-box' }
    const outerNext: Record<string, unknown> = { ...panelStyle }
    for (const k of moveKeys) {
      if ((panelStyle as Record<string, unknown>)[k] !== undefined) {
        next[k] = (panelStyle as Record<string, unknown>)[k]
      }
      delete outerNext[k]
    }
    next.minHeight = (panelStyle as { minHeight?: number | string }).minHeight ?? 0
    delete outerNext.minHeight
    outerPanel = outerNext as CSSProperties
    innerStyle = next as CSSProperties
  }

  // Backdrop sizes to the visual viewport so the modal recenters above the
  // on-screen keyboard. We keep centered alignment (instead of toggling to
  // top-anchored when the keyboard is up) because an `align-items` switch
  // isn't transitionable, and a smooth slide reads better than a jump. The
  // center of the shrunk rect lands above the keyboard, which is enough room
  // for the picker dropdown in practice.
  const rect = vv ?? { top: 0, left: 0, width: 0, height: 0 }
  const usingVv = vv != null
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed',
      top: usingVv ? rect.top : 0,
      left: usingVv ? rect.left : 0,
      width: usingVv ? rect.width : '100%',
      height: usingVv ? rect.height : '100%',
      zIndex,
      background: 'rgba(20,14,8,0.42)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: backdropPadding,
      opacity: visible ? 1 : 0,
      // Smooth the backdrop's resize when the on-screen keyboard opens or
      // closes — without this the modal snaps to its new position the moment
      // visualViewport fires (often a single end-of-animation event on iOS).
      // The duration matches the panel's own transform/height transition.
      // Never capture clicks while invisible. If for any reason the
      // close-transition timeout doesn't unmount us, an invisible backdrop
      // would otherwise eat every click on the page underneath.
      pointerEvents: visible ? 'auto' : 'none',
      transition:
        `opacity ${FADE_MS}ms ease, ` +
        `top ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), ` +
        `left ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), ` +
        `width ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), ` +
        `height ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.surface,
        borderRadius: theme.radiusLg,
        border: theme.cardBorder,
        boxShadow: '0 20px 60px rgba(20,14,8,0.30)',
        // autoHeight: outer is a plain block so the measured inner isn't a
        // flex item that gets squeezed by outer's height transition (which
        // creates a feedback loop with the ResizeObserver). Overflow stays
        // visible so absolutely-positioned popovers inside the modal
        // (e.g. IngredientPicker's dropdown) can escape the panel bounds;
        // modals that genuinely need clipping can set overflow in panelStyle.
        // !autoHeight: outer is the flex column, like before.
        display: autoHeight ? 'block' : 'flex', flexDirection: 'column',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(16px)',
        transformOrigin: 'center',
        transition:
          `opacity ${FADE_MS}ms ease, ` +
          `transform ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), ` +
          `width ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), max-width ${PANEL_MS}ms ease, ` +
          `height ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), max-height ${PANEL_MS}ms ease`,
        ...outerPanel,
        ...(autoHeight && innerH != null ? { height: innerH } : null),
      }}>
        {autoHeight
          ? <div ref={innerRef} style={innerStyle}>{children}</div>
          : children}
      </div>
    </div>,
    document.body,
  )
}
